import {DatePicker} from '@github-ui/date-picker'
import {useMemo, useState} from 'react'

import {formatDateString} from '../../utils/date-formatting'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import {LABELS} from './constants/labels'
import fieldEditorsStyles from './FieldEditors.module.css'

const DateDisplay = ({formattedDate}: {formattedDate: string}) => (
  <span className={fieldEditorsStyles.dateText}>{formattedDate || LABELS.setDate}</span>
)

type IssueFieldDateEditorProps = {
  fieldId: string
  fieldName: string
  value: Date | null
  readonly: boolean
  onCommit: (fieldId: string, value: Date | null) => void
  hideDivider: boolean
}

export const IssueFieldDateEditor = ({
  fieldId,
  fieldName,
  value,
  readonly,
  onCommit,
  hideDivider,
}: IssueFieldDateEditorProps) => {
  const formattedDate = useMemo(() => {
    if (!value) return ''
    return formatDateString(value)
  }, [value])

  const [isOpen, setIsOpen] = useState(false)

  const handleChange = (date: Date | null) => {
    onCommit(fieldId, date)
    setIsOpen(false)
  }

  return (
    <Section
      sectionHeader={
        <SectionHeader
          title={fieldName}
          readonly={readonly}
          buttonProps={readonly ? undefined : {onClick: () => setIsOpen(true)}}
        />
      }
      hideDivider={hideDivider}
    >
      {readonly ? (
        <div className={fieldEditorsStyles.dateAnchorContainer}>
          <DateDisplay formattedDate={formattedDate} />
        </div>
      ) : (
        <DatePicker
          key={fieldName}
          anchor={props => (
            <div
              {...props}
              className={`${fieldEditorsStyles.dateAnchorContainer} ${fieldEditorsStyles.dateAnchorContainerInteractive}`}
            >
              <DateDisplay formattedDate={formattedDate} />
            </div>
          )}
          showClearButton
          showTodayButton
          variant="single"
          dateFormat="long"
          onChange={handleChange}
          value={value}
          placeholder="Set date"
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
        />
      )}
    </Section>
  )
}

try{ DateDisplay.displayName ||= 'DateDisplay' } catch {}
try{ IssueFieldDateEditor.displayName ||= 'IssueFieldDateEditor' } catch {}